import { useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { fmtDate } from '../lib/format';
import { eurC, inEurZahl } from '../lib/money';
import { ChipRadios } from './ChipRadios';
import { KUNDEN_ARTEN, kundeWerteOf, LEERER_KUNDE, tageZwischen, type Art, type KundeWerte } from './formular';
import { EU_LAENDER } from './laender';
import { LAENDER_WELT } from './laender-welt';
import type { Absender, Kunde, RtItem } from './types';
import { VorschlagInput, type Vorschlag } from './Vorschlaege';
import './formular.css';

// Kunde der Rechnung (auch für Angebote und wiederkehrende Rechnungen): aus der Kundenliste suchen (Nummer, Name, Ort)
// oder neu eintippen. Gewählt: alle Felder gefüllt und kundeId gesetzt; die Felder bleiben änderbar (gilt nur für
// diese Rechnung). Ein neuer Kunde landet beim Schreiben von selbst in der Liste. Warnung, wenn der Kunde überfällige
// Rechnungen hat oder langsam zahlt.
//
// Props: kunden (data.kunden), items (data.items, für die Warnung; ohne: keine Warnung), today, value/onChange (der
// ganze Kunde; beim Wählen aus der Liste kommt erst onChange mit dem Kunden, dann onPick), onPick (Kunde aus der Liste
// gewählt, null: „Anderen Kunden wählen“, z. B. um Zahlungsziel und XRechnung aus dem Kunden zu übernehmen). Ohne
// Erklärungen zu Reverse charge oder Verzug (Sam 13.10.: so einfach wie bei Lexware, das Rechtliche regelt die App).
// ohneArt, ohneZusatz: ohne die Wahl der Kundenart bzw. das Feld „Zusatz“ (kompaktes Formular in Billtoro, dort
// stehen sie hinter „andere Kundenart“ bzw. „Weitere Angaben“). Kunde außerhalb der EU: Land, Firma oder Privatperson,
// Steuernummer (nur Firma) und PLZ optional.
export function KundeWahl({
  kunden,
  items,
  today,
  value,
  onChange,
  onPick,
  ohneArt,
  ohneZusatz,
}: {
  kunden: Kunde[];
  items?: RtItem[];
  today: string;
  value: KundeWerte;
  onChange(v: KundeWerte): void;
  onPick?(k: Kunde | null): void;
  ohneArt?: boolean;
  ohneZusatz?: boolean;
}) {
  const nameRef = useRef<HTMLInputElement>(null);
  const w = value;
  const set = (patch: Partial<KundeWerte>) => onChange({ ...w, ...patch });
  const gewaehlt = w.kundeId ? (kunden.find((k) => k.id === w.kundeId) ?? null) : null;

  // Vorschläge aus der Kundenliste, solange kein Kunde gewählt ist
  const treffer = useMemo(() => (w.kundeId ? [] : suche(kunden, w.name)), [kunden, w.kundeId, w.name]);
  const vorschlaege: Vorschlag[] = treffer.map((k) => ({
    key: k.id!,
    label: (
      <>
        {k.nummer != null && <span className="kw-nr num">{k.nummer}</span>} {k.name}
      </>
    ),
    sub: [k.street, `${k.zip} ${k.city}`].filter((x) => x.trim()).join(', '),
  }));

  function pick(id: string) {
    const k = kunden.find((x) => x.id === id);
    if (!k) return;
    onChange(kundeWerteOf(k));
    onPick?.(k);
  }

  function anderer() {
    onChange({ ...LEERER_KUNDE });
    onPick?.(null);
    nameRef.current?.focus();
  }

  const warnung = useMemo(() => (gewaehlt && items ? warnungOf(gewaehlt.id!, items, today) : null), [gewaehlt, items, today]);

  return (
    <div className="kw">
      {!ohneArt && <ChipRadios label="Kundenart" className="rt-kart" value={w.art} options={KUNDEN_ARTEN} onChange={(art: Art) => set({ art })} />}
      {gewaehlt && (
        <p className="kw-aus">
          <span>
            Kunde <b className="num">{gewaehlt.nummer}</b> aus der Kundenliste
          </span>
          <button type="button" className="linkbtn" onClick={anderer}>
            Anderen Kunden wählen
          </button>
        </p>
      )}
      {warnung && <div className="notice warn kw-warn">{warnung}</div>}
      {gewaehlt?.notiz && (
        <p className="kw-notiz">
          <b>Ihre Notiz:</b> {gewaehlt.notiz}
        </p>
      )}
      <div className="rt-grid">
        <label className="field rt-span2">
          <span>Name oder Firma</span>
          <VorschlagInput
            value={w.name}
            inputRef={nameRef}
            vorschlaege={vorschlaege}
            onPick={pick}
            // Name geändert: nicht mehr der Kunde aus der Liste (sonst ginge die Rechnung an den alten)
            onChange={(name) => set({ name, kundeId: null })}
            inputProps={{
              'aria-label': 'Name oder Firma',
              placeholder: kunden.length ? 'Tippen: Name, Kundennummer oder Ort' : undefined,
            }}
          />
        </label>
        {!ohneZusatz && (
          <label className="field rt-span2">
            <span>Zusatz (optional)</span>
            <input value={w.extra} placeholder="z. B. z. Hd. Frau Meier" onChange={(e) => set({ extra: e.target.value })} />
          </label>
        )}
        <label className="field rt-span2">
          <span>Straße und Hausnummer</span>
          <input value={w.street} onChange={(e) => set({ street: e.target.value })} />
        </label>
        <label className="field">
          <span>{w.art === 'welt' ? 'PLZ (optional)' : 'PLZ'}</span>
          <input value={w.zip} inputMode={w.art === 'eu' || w.art === 'welt' ? 'text' : 'numeric'} onChange={(e) => set({ zip: e.target.value })} />
        </label>
        <label className="field">
          <span>Ort</span>
          <input value={w.city} onChange={(e) => set({ city: e.target.value })} />
        </label>
        {w.art === 'eu' && (
          <label className="field rt-span2">
            <span>Land</span>
            <select value={w.country} onChange={(e) => set({ country: e.target.value })}>
              {EU_LAENDER.map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        )}
        {w.art === 'welt' && (
          <>
            <label className="field rt-span2">
              <span>Land</span>
              <select value={w.land} onChange={(e) => set({ land: e.target.value })}>
                {LAENDER_WELT.map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <div className="field rt-span2">
              <ChipRadios
                label="Firma / Privatperson"
                value={w.weltPrivat ? 'privat' : 'firma'}
                options={WELT_ARTEN}
                onChange={(a) => set({ weltPrivat: a === 'privat' })}
              />
            </div>
            {!w.weltPrivat && (
              <label className="field rt-span2">
                <span>Steuernummer des Kunden (optional)</span>
                <input value={w.vatId} maxLength={20} autoComplete="off" onChange={(e) => set({ vatId: e.target.value })} />
              </label>
            )}
          </>
        )}
        {(w.art === 'firma' || w.art === 'eu') && (
          <label className="field rt-span2">
            <span>USt-IdNr. des Kunden{w.art === 'eu' ? '' : ' (optional)'}</span>
            <input
              value={w.vatId}
              placeholder={w.art === 'eu' ? `z. B. ${EU_LAENDER.find((l) => l[0] === w.country)?.[2] ?? ''}…` : 'z. B. DE123456789'}
              autoComplete="off"
              onChange={(e) => set({ vatId: e.target.value })}
            />
            {w.art === 'eu' && (
              <small>
                <a href="https://ec.europa.eu/taxation_customs/vies/" target="_blank" rel="noreferrer">
                  USt-IdNr. prüfen (EU)
                </a>
              </small>
            )}
          </label>
        )}
      </div>
    </div>
  );
}

const WELT_ARTEN: [('firma' | 'privat'), string][] = [
  ['firma', 'Firma'],
  ['privat', 'Privatperson'],
];

// XRechnung für Behörden (Sam: „iwo klein, Normalfall ist die normale“): ein kleiner Haken. Normal geht jede Rechnung
// als PDF mit der E-Rechnung darin (ZUGFeRD). Mit Haken: Leitweg-ID und E-Mail des Kunden; fehlen eigene Angaben dafür,
// sagt es der Hinweis. Props: on/onChange (Haken), value/onChange des Kunden (Leitweg-ID, E-Mail), absender.
export function XRechnungWahl({
  on,
  onToggle,
  value,
  onChange,
  absender,
}: {
  on: boolean;
  onToggle(on: boolean): void;
  value: KundeWerte;
  onChange(v: KundeWerte): void;
  absender: Pick<Absender, 'email' | 'phone'>;
}) {
  // Für XRechnung braucht es E-Mail und Telefon des Absenders
  const fehlt = [!absender.email && 'Ihre E-Mail', !absender.phone && 'Ihre Telefonnummer'].filter(Boolean);
  return (
    <div className="kw-xr">
      <label className="check">
        <input type="checkbox" checked={on} onChange={(e) => onToggle(e.target.checked)} />
        <span>XRechnung (für Behörden)</span>
      </label>
      {on && (
        <div className="rt-grid rt-xr">
          <label className="field rt-span2">
            <span>Leitweg-ID</span>
            <input
              value={value.buyerRef}
              placeholder="z. B. 04011000-1234512345-06"
              onChange={(e) => onChange({ ...value, buyerRef: e.target.value })}
            />
            <small>Steht im Auftrag der Behörde. Firmen, die XRechnung wollen, nennen hier ihre Bestellnummer.</small>
          </label>
          <label className="field rt-span2">
            <span>E-Mail des Kunden für Rechnungen</span>
            <input
              type="email"
              value={value.email}
              placeholder="z. B. rechnung@stadt.de"
              onChange={(e) => onChange({ ...value, email: e.target.value })}
            />
          </label>
          {fehlt.length > 0 && (
            <div className="notice warn rt-span4">
              Für die XRechnung fehlt in <Link to="/rechnung/angaben">Meine Angaben</Link> noch: {fehlt.join(' und ')}.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Suche in der Kundenliste: Kundennummer (Anfang), Name, Ort oder PLZ; Namen, die so anfangen, zuerst
export function suche(kunden: Kunde[], q: string, max = 8): Kunde[] {
  const t = q.trim().toLowerCase();
  if (!t) return [];
  const nr = /^\d+$/.test(t);
  const hits = kunden.filter(
    (k) =>
      k.id &&
      ((nr && String(k.nummer ?? '').startsWith(t)) ||
        k.name.toLowerCase().includes(t) ||
        k.city.toLowerCase().includes(t) ||
        (nr && k.zip.startsWith(t))),
  );
  const start = (k: Kunde) => (k.name.toLowerCase().startsWith(t) || String(k.nummer ?? '') === t ? 0 : 1);
  return hits.sort((a, b) => start(a) - start(b)).slice(0, max);
}

// Warnung zum Kunden aus seinen Rechnungen: überfällige (mit Betrag; alle in einer Währung: in dieser, sonst in Euro zum
// Kurs der Rechnungen) oder langsam gezahlt (bezahlte Rechnungen der letzten 12 Monate im Schnitt mehr als 7 Tage nach
// Fälligkeit, wie in der Kundenliste)
export function warnungOf(kundeId: string, items: RtItem[], today: string): string | null {
  const mine = items.filter((i) => i.kundeId === kundeId && i.kind === 'rechnung');
  const ueber = mine.filter((i) => (i.status === 'offen' || i.status === 'teilweise') && i.dueDate < today && i.offen > 0);
  if (ueber.length) {
    const eine = ueber.every((i) => i.waehrung === ueber[0]!.waehrung);
    const sum = eine ? ueber.reduce((s, i) => s + i.offen, 0) : ueber.reduce((s, i) => s + inEurZahl(i.offen, i.kurs), 0);
    const alt = ueber.reduce((a, i) => (i.dueDate < a ? i.dueDate : a), ueber[0]!.dueDate);
    return `Achtung: ${ueber.length === 1 ? '1 Rechnung' : `${ueber.length} Rechnungen`} an diesen Kunden ${
      ueber.length === 1 ? 'ist' : 'sind'
    } überfällig (zusammen ${eine ? eurC(sum, ueber[0]!.waehrung) : `${eurC(sum)} umgerechnet`}, fällig seit ${fmtDate(alt)}).`;
  }
  const bezahlt = mine.filter(
    (i) => i.status === 'bezahlt' && i.paidOn && i.amount > 0 && i.bezahlt > i.ausgebucht && tageZwischen(i.paidOn, today) <= 365,
  );
  if (!bezahlt.length) return null;
  const verzug = bezahlt.reduce((s, i) => s + tageZwischen(i.dueDate, i.paidOn!), 0) / bezahlt.length;
  if (verzug <= 7) return null;
  return `Dieser Kunde zahlt langsam: im Schnitt ${Math.round(verzug)} Tage nach der Fälligkeit.`;
}
